// src/hooks/useMarketData.js
// Client-side hook that fetches ATR + VIX from our Vercel serverless function
// Caches results for 15 minutes to avoid hammering the API

const CACHE_DURATION_MS = 15 * 60 * 1000; // 15 minutes
const cache = {};

async function fetchMarketData(asset) {
  const now = Date.now();
  const cached = cache[asset];

  // Return cached data if still fresh
  if (cached && now - cached.timestamp < CACHE_DURATION_MS) {
    return { ...cached.data, fromCache: true };
  }

  const res = await fetch(`/api/marketData?asset=${asset}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Unknown error" }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }

  const data = await res.json();

  // Store in cache
  cache[asset] = { data, timestamp: now };

  return { ...data, fromCache: false };
}

// React hook
import { useState, useEffect, useCallback } from "react";

export function useMarketData(asset) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  const fetch = useCallback(async (forceRefresh = false) => {
    if (!asset) return;

    // Clear cache if force refresh
    if (forceRefresh && cache[asset]) {
      delete cache[asset];
    }

    setLoading(true);
    setError(null);

    try {
      const result = await fetchMarketData(asset);
      setData(result);
      setLastFetched(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [asset]);

  // Fetch on mount and when asset changes
  useEffect(() => {
    fetch();
  }, [fetch]);

  return {
    // ATR values
    atr5: data?.atr5 ?? null,
    atr14: data?.atr14 ?? null,

    // VIX values
    vix: data?.vix?.current ?? null,
    vix20MA: data?.vix?.ma20 ?? null,
    vix5MA: data?.vix?.ma5 ?? null,

    // Metadata
    asOf: data?.asOf ?? null,
    lastPrice: data?.lastPrice ?? null,
    ticker: data?.ticker ?? null,
    fromCache: data?.fromCache ?? false,
    note: data?.note ?? null,

    // State
    loading,
    error,
    lastFetched,

    // Actions
    refresh: () => fetch(true),
  };
}
